import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { getAssessment, updateAssessment } from '../../api/assessmentApi';
import Spinner from '../../components/ui/Spinner';
import Card from '../../components/ui/Card';

/* ── Yup schema ──────────────────────────────────────────────────── */
const schema = yup.object({
  title: yup
    .string()
    .required('Title is required')
    .min(2, 'Title must be at least 2 characters'),
  description: yup.string().nullable(),
  bootcamp_id: yup.string().nullable(),
  time_limit_minutes: yup
    .number()
    .nullable()
    .min(1, 'Must be at least 1 minute')
    .transform((v) => (isNaN(v) ? null : v)),
  max_attempts: yup
    .number()
    .nullable()
    .min(1, 'Must be at least 1')
    .transform((v) => (isNaN(v) ? null : v)),
  is_anonymous: yup.boolean(),
});

export default function EditAssessmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loadingAssessment, setLoadingAssessment] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title:              '',
      description:        '',
      bootcamp_id:        '',
      time_limit_minutes: '',
      max_attempts:       '',
      is_anonymous:       false,
    },
  });

  useEffect(() => {
    setLoadingAssessment(true);
    getAssessment(id)
      .then((res) => {
        const a = res.data?.data ?? res.data;
        reset({
          title:              a.title ?? '',
          description:        a.description ?? '',
          bootcamp_id:        a.bootcamp_id ?? '',
          time_limit_minutes: a.time_limit_minutes ?? '',
          max_attempts:       a.max_attempts ?? '',
          is_anonymous:       !!a.is_anonymous,
        });
      })
      .catch(() => setFetchError('Failed to load assessment'))
      .finally(() => setLoadingAssessment(false));
  }, [id, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data };
      // Remove empty optional fields
      if (!payload.time_limit_minutes) delete payload.time_limit_minutes;
      if (!payload.max_attempts) delete payload.max_attempts;
      if (!payload.bootcamp_id) delete payload.bootcamp_id;
      if (!payload.description) delete payload.description;

      await updateAssessment(id, payload);
      toast.success('Assessment updated successfully');
      navigate(`/assessments/${id}`);
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to update assessment';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ── Loading skeleton ──────────────────────────────────────────── */
  if (loadingAssessment) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <div className="h-6 w-40 rounded bg-neutral-200 animate-pulse" />
        <div className="h-8 w-56 rounded bg-neutral-200 animate-pulse" />
        <div className="rounded-card bg-white shadow-card ring-1 ring-neutral-200/60 p-6 space-y-5 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-4 w-24 rounded bg-neutral-100" />
              <div className="h-10 w-full rounded bg-neutral-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ── Error state ────────────────────────────────────────────────── */
  if (fetchError) {
    return (
      <div className="p-6 text-center">
        <p className="text-error-600 mb-4">{fetchError}</p>
        <Link to={`/assessments/${id}`} className="btn-secondary text-sm">
          ← Back to Assessment
        </Link>
      </div>
    );
  }

  /* ── Form ───────────────────────────────────────────────────────── */
  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Back link + heading */}
      <div>
        <Link
          to={`/assessments/${id}`}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Assessment
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 font-heading">Edit Assessment</h1>
        <p className="text-sm text-neutral-500 mt-0.5">
          Update the details for this assessment.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Title */}
          <div>
            <label className="label-base" htmlFor="edit-title">
              Title <span className="text-error-500">*</span>
            </label>
            <input
              id="edit-title"
              {...register('title')}
              className={clsx('input-base', errors.title && 'border-error-400 focus:border-error-500 focus:ring-error-500')}
              placeholder="Assessment title"
            />
            {errors.title && (
              <p className="mt-1 text-xs text-error-600">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="label-base" htmlFor="edit-description">Description</label>
            <textarea
              id="edit-description"
              rows={3}
              {...register('description')}
              className="input-base resize-none"
              placeholder="Optional description…"
            />
          </div>

          {/* Bootcamp ID */}
          <div>
            <label className="label-base" htmlFor="edit-bootcamp_id">Bootcamp ID (optional)</label>
            <input
              id="edit-bootcamp_id"
              {...register('bootcamp_id')}
              className="input-base"
              placeholder="e.g. BC-2024-01"
            />
          </div>

          {/* Time limit + Max attempts row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="label-base" htmlFor="edit-time_limit_minutes">
                Time Limit (minutes)
              </label>
              <input
                id="edit-time_limit_minutes"
                type="number"
                min={1}
                {...register('time_limit_minutes')}
                className={clsx('input-base', errors.time_limit_minutes && 'border-error-400')}
                placeholder="No limit"
              />
              {errors.time_limit_minutes && (
                <p className="mt-1 text-xs text-error-600">{errors.time_limit_minutes.message}</p>
              )}
            </div>

            <div>
              <label className="label-base" htmlFor="edit-max_attempts">Max Attempts</label>
              <input
                id="edit-max_attempts"
                type="number"
                min={1}
                {...register('max_attempts')}
                className={clsx('input-base', errors.max_attempts && 'border-error-400')}
                placeholder="Unlimited"
              />
              {errors.max_attempts && (
                <p className="mt-1 text-xs text-error-600">{errors.max_attempts.message}</p>
              )}
            </div>
          </div>

          {/* Anonymous toggle */}
          <div className="flex items-center gap-3 py-1">
            <label htmlFor="edit-is_anonymous" className="flex items-center gap-3 cursor-pointer select-none">
              <input
                id="edit-is_anonymous"
                type="checkbox"
                {...register('is_anonymous')}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-neutral-700">Anonymous responses</span>
            </label>
            <span className="text-xs text-neutral-400">Participant identities will not be recorded</span>
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-100 pt-4 flex items-center justify-between gap-4">
            <Link
              to={`/assessments/${id}`}
              className="btn-secondary text-sm"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={saving || !isDirty}
            >
              {saving && <Spinner size="sm" />}
              Save Changes
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
